import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { SecondaryButton } from '../index';

/**
 * ConfirmationModal Component
 * What: Reusable confirmation modal for user actions
 * Why: Provides consistent confirmation UI across the application
 * Where: shared/components/ui/modal/ConfirmationModal.jsx
 * 
 * Props:
 * - onClose: Function to close modal
 * - onConfirm: Function to execute on confirmation
 * - title: Modal title (defaults to "Confirm Action")
 * - message: Custom confirmation message
 * - confirmText: Text for confirm button (defaults to "Confirm")
 * - cancelText: Text for cancel button (defaults to "Cancel")
 * - variant: 'danger' | 'warning' | 'info' (defaults to 'warning')
 * - data: Additional data passed from modal host
 */
export const ConfirmationModal = ({ 
  onClose, 
  onConfirm, 
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = 'warning',
  data = {}
}) => {
  // Extract custom props from data if provided
  const {
    title: dataTitle,
    message: dataMessage,
    confirmText: dataConfirmText,
    cancelText: dataCancelText,
    variant: dataVariant,
    onConfirm: dataOnConfirm
  } = data;

  // Use data values if provided, otherwise fall back to direct props
  const finalTitle = dataTitle || title;
  const finalMessage = dataMessage || message;
  const finalConfirmText = dataConfirmText || confirmText;
  const finalCancelText = dataCancelText || cancelText;
  const finalVariant = dataVariant || variant;
  const finalOnConfirm = dataOnConfirm || onConfirm;

  const handleConfirm = () => {
    if (finalOnConfirm && typeof finalOnConfirm === 'function') {
      finalOnConfirm();
    }
    onClose();
  };

  const getVariantStyles = () => {
    switch (finalVariant) {
      case 'danger':
        return {
          icon: 'text-error-600',
          bg: 'bg-error-100',
          button:
            'border-2 bg-error-600 text-white border-error-600 hover:bg-error-700 hover:text-white focus:ring-error-500'
        };
      case 'warning':
        return {
          icon: 'text-warning-600',
          bg: 'bg-warning-100',
          button:
            'border-2 bg-warning-600 text-white border-warning-600 hover:bg-warning-700 hover:text-white focus:ring-warning-500'
        };
      case 'info':
        return {
          icon: 'text-primary-600',
          bg: 'bg-primary-100',
          button:
            'border-2 bg-primary-600 text-white border-primary-600 hover:bg-primary-700 hover:text-white focus:ring-primary-500'
        };
      default:
        return {
          icon: 'text-warning-600',
          bg: 'bg-warning-100',
          button:
            'border-2 bg-warning-600 text-white border-warning-600 hover:bg-warning-700 hover:text-white focus:ring-warning-500'
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <div className="text-center">
      {/* Icon */}
      <div className={`mx-auto flex items-center justify-center w-16 h-16 ${styles.bg} rounded-full mb-4`}>
        <AlertTriangle className={`w-8 h-8 ${styles.icon}`} />
      </div>

      {/* Title */}
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        {finalTitle}
      </h3>

      {/* Message */}
      <p className="text-gray-600 mb-6">
        {finalMessage}
      </p>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <SecondaryButton
          onClick={onClose}
          className="flex-1"
        >
          {finalCancelText}
        </SecondaryButton>
        
        <button
          onClick={handleConfirm}
          className={`flex-1 inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-md ${styles.button}`}
        >
          {finalConfirmText}
        </button>
      </div>
    </div>
  );
};
